import React, { useState, useEffect } from 'react';
import { Shield } from 'lucide-react';
import { usePermissions } from '../../contexts/PermissionContext';
import { toast } from 'react-toastify';
import RoleList from './components/RoleList';
import RoleUserList from './components/RoleUserList';
import RoleEditorModal from './components/RoleEditorModal';

const RoleManagement = () => {
  const {
    getAllRoles,
    createRole,
    updateRole,
    deleteRole,
    getUsersByRole,
    hasPermission,
    PERMISSIONS
  } = usePermissions();

  const [roles, setRoles] = useState([]);
  const [selectedRole, setSelectedRole] = useState(null);
  const [roleUsers, setRoleUsers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [loadingUsers, setLoadingUsers] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingRole, setEditingRole] = useState(null);

  const allPermissions = Object.values(PERMISSIONS);

  // Load roles
  const loadRoles = async () => {
    try {
      setLoading(true);
      const rolesData = await getAllRoles();
      setRoles(rolesData || []);
    } catch (error) {
      console.error('Load roles error:', error);
      toast.error('Roller yüklenirken hata oluştu');
    } finally {
      setLoading(false);
    }
  };

  // Load users for a role
  const loadRoleUsers = async (roleName) => {
    try {
      setLoadingUsers(true);
      const users = await getUsersByRole(roleName);
      setRoleUsers(users || []);
    } catch (error) {
      console.error('Load role users error:', error);
      toast.error('Rol kullanıcıları yüklenirken hata oluştu');
      setRoleUsers([]);
    } finally {
      setLoadingUsers(false);
    }
  };

  useEffect(() => {
    loadRoles();
  }, []);

  const handleSelectRole = (role) => {
    setSelectedRole(role);
    if (role) {
      loadRoleUsers(role.name);
    } else {
      setRoleUsers([]);
    }
  };

  const handleCreateRole = () => {
    setEditingRole(null);
    setDialogOpen(true);
  };

  const handleEditRole = (role) => {
    setEditingRole(role);
    setDialogOpen(true);
  };

  const handleSaveRole = async (roleData) => {
    try {
      if (editingRole) {
        await updateRole(editingRole.id, roleData);
        toast.success('Rol güncellendi');
      } else {
        await createRole(roleData);
        toast.success('Rol oluşturuldu');
      }
      setDialogOpen(false);
      loadRoles();
      // If we edited the selected role, refresh it
      if (selectedRole && editingRole && selectedRole.id === editingRole.id) {
        // Refresh logic if needed, or just update local state
        // If name changed, we might need to reload users if dependent on name
        if (selectedRole.name !== roleData.name) {
          setSelectedRole({ ...selectedRole, ...roleData });
          loadRoleUsers(roleData.name);
        }
      }
    } catch (error) {
      console.error('Save role error:', error);
      const msg = error.response?.data?.message || 'Rol kaydedilirken hata oluştu';
      toast.error(msg);
    }
  };

  const handleDeleteRole = async (role) => {
    if (window.confirm(`${role.name} rolünü silmek istediğinizden emin misiniz?`)) {
      try {
        await deleteRole(role.id);
        toast.success('Rol silindi');
        if (selectedRole?.id === role.id) {
          setSelectedRole(null);
          setRoleUsers([]);
        }
        loadRoles();
      } catch (error) {
        toast.error('Rol silinirken hata oluştu');
      }
    }
  };

  if (!hasPermission(PERMISSIONS.USER_ASSIGN_ROLE)) {
    return (
      <div className="p-6">
        <div className="bg-red-500/10 border border-red-500/20 text-red-400 p-4 rounded-xl flex items-center gap-3">
          <Shield className="w-5 h-5" />
          <p>Bu sayfaya erişim yetkiniz bulunmamaktadır.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6 animate-fadeIn h-[calc(100vh-80px)] flex flex-col">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 shrink-0">
        <div>
          <h1 className="text-2xl font-bold text-white flex items-center gap-2">
            <Shield className="w-8 h-8 text-orange-500" />
            Rol Yönetimi
          </h1>
          <p className="text-gray-400 mt-1">Sistem rollerini ve yetkilerini yönetin</p>
        </div>

        <button
          onClick={handleCreateRole}
          className="flex items-center justify-center gap-2 px-4 py-2 bg-orange-600 hover:bg-orange-700 text-white rounded-lg transition-colors font-medium shadow-lg shadow-orange-500/20"
        >
          <Shield className="w-4 h-4" />
          Yeni Rol Oluştur
        </button>
      </div>

      {/* Content Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 flex-1 min-h-0">
        {/* Left Column: Roles List */}
        <div className="lg:col-span-2 h-full min-h-0">
          <RoleList
            roles={roles}
            selectedRole={selectedRole}
            onSelectRole={handleSelectRole}
            onEditRole={handleEditRole}
            onDeleteRole={handleDeleteRole}
            loading={loading}
          />
        </div>

        {/* Right Column: Role Users */}
        <div className="h-full min-h-0">
          <RoleUserList
            users={roleUsers}
            roleName={selectedRole?.name}
            loading={loadingUsers}
          />
        </div>
      </div>

      <RoleEditorModal
        isOpen={dialogOpen}
        onClose={() => setDialogOpen(false)}
        role={editingRole}
        onSave={handleSaveRole}
        allPermissions={allPermissions}
      />
    </div>
  );
};

export default RoleManagement;

